import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { AvailableCurrencyFragment, PaymentsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { NewPaymentModal } from 'modules/banking/components/NewPaymentModal'
import { NewPaymentModalF0 } from 'modules/banking/components/NewPaymentModal/NewPaymentModalF0'
import { PaymentsDataCollection } from 'modules/banking/components/PaymentsDataCollection'
import { PaymentsDataTable } from 'modules/banking/components/PaymentsDataTable'
import { AllPaymentsInsightCards } from 'modules/banking/components/TreasuryInsightCard'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

// NOTE: This FF enables the new PaymentsDataCollection component which supports
// nested PaymentRequest children rows. For the children to be visible, the
// 'dev_banking_payment_requests' FF must ALSO be enabled. If you enable this FF
// without enabling 'dev_banking_payment_requests', the PaymentRequest children
// won't appear in the table.
const FEATURE_FLAG_NAME = 'dev_payments_enable_datacollection'
const TRANSFER_FEATURE_FLAG_NAME = 'dev_finance_swan_reactivation'

const PaymentsPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const currency = company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'

  // Read status filter from URL params (e.g., when navigating from Overview widget)
  const { status: statusParam } = useSearchParams<{ status?: string }>()
  const initialStatusFilter = useMemo(() => {
    if (statusParam) {
      return { status: [statusParam] }
    }
    return undefined
  }, [statusParam])

  // Track dueDate filter from DataCollection to pass to InsightCards
  const [dueDateFilter, setDueDateFilter] = useState<{ between: [string, string] } | undefined>()

  const handleDueDateFilterChange = useCallback(
    (filter: { between: [string, string] } | undefined) => {
      setDueDateFilter(filter)
    },
    []
  )

  const { data, loading, error } = useQuery(PaymentsPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [TRANSFER_FEATURE_FLAG_NAME, FEATURE_FLAG_NAME],
    },
  })

  const canSeeSepaTransferAction =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === TRANSFER_FEATURE_FLAG_NAME
    )?.enabled ?? false

  const isDataCollectionEnabled =
    data?.features?.featuresConnection?.nodes.find((feature) => feature.name === FEATURE_FLAG_NAME)
      ?.enabled ?? false

  const availableCurrencies: AvailableCurrencyFragment[] =
    data?.apiCore?.currenciesConnection?.edges?.map((edge) => edge.node) ?? []

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [newPaymentModalOpen, toggleNewPaymentModal] = useToggle(false)

  const outstandingTransfersCount = data?.banking.transfersConnection?.totalCount || 0

  const handleOnCreationSuccess = (newPaymentId: number) => {
    toggleNewPaymentModal(false)()

    if (outstandingTransfersCount > 0) {
      navigate(
        navigation.finance.banking.payments.transfers.fullPath() + '?paymentId=' + newPaymentId
      )
    }
  }

  const canSeePaymentsModule =
    !!data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can

  useUnsafeEffect(() => {
    if (!loading) {
      const paymentsCount = data?.banking.paymentsConnection?.aggregate?.count || 0
      const paymentsStatuses =
        data?.banking.paymentsConnection?.aggregate?.terms?.status?.values || []

      const user_making_payment = canSeePaymentsModule ? 'yes' : 'no'

      track(trackingEvents.accounting.payments.sectionBulkPayments, {
        user_making_payment: user_making_payment,
        has_data: paymentsCount > 0,
        bulk_payments_count: paymentsCount,
        bulk_payments_status: paymentsStatuses,
      })
    }
  }, [canSeePaymentsModule, data, loading])

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={canSeePaymentsModule}
      variant="full"
    >
      <Box flexDirection="column" gap="s16" width="full" height="full">
        {isDataCollectionEnabled ? (
          <>
            <AllPaymentsInsightCards
              currency={currency}
              locale={locale}
              dueDateFilter={dueDateFilter}
            />
            <PaymentsDataCollection
              companyId={company.id}
              locale={locale}
              canSeeSepaTransferAction={canSeeSepaTransferAction}
              onNewPaymentAction={toggleNewPaymentModal(true)}
              onDueDateFilterChange={handleDueDateFilterChange}
              initialFilters={initialStatusFilter}
            />
          </>
        ) : (
          <PaymentsDataTable
            onNewPaymentAction={toggleNewPaymentModal(true)}
            locale={locale}
            currency={currency}
            availableCurrencies={availableCurrencies}
            canSeeSepaTransferAction={canSeeSepaTransferAction}
          />
        )}
        {newPaymentModalOpen &&
          (isDataCollectionEnabled ? (
            <NewPaymentModalF0
              onSuccess={handleOnCreationSuccess}
              onClose={toggleNewPaymentModal(false)}
              enablePaymentMethodFilter={canSeeSepaTransferAction}
            />
          ) : (
            <NewPaymentModal
              onSuccess={handleOnCreationSuccess}
              onClose={toggleNewPaymentModal(false)}
            />
          ))}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PaymentsPage
